import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FormikProps, Formik, Form, Field } from "formik";
import { Col, Row } from "react-bootstrap";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { capitalize, map, pickBy } from "lodash";
import { InputCheckboxField } from "../../../../components/Shared/InputCheckboxField";
import * as XLSX from "xlsx";
import qs from "qs";
import { request } from "../../../../utils/Request";
import { getOrderStatus } from "../../../../components/Common/Display/OrderStatus";
import { getLocation } from "../../../../components/Common/Display/Location";
import { utcDateToLocalDate } from "../../../../utils/Helper";

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    dropdowns: any;
    visibility: any;
    toastify: any;
    onClose?: any;
}

type TInputForm = {
    consider_all_pages: boolean,
    type: boolean,
    mode_of_transport: boolean,
    truck_plate_number: boolean,
    trailer_plate_number: boolean,
    connect_to_cvs: boolean,
    is_active: boolean,
    capacity: boolean,
    euro_norm: boolean,
    legal_entity: boolean,
    trailer_capacity: boolean,
    sub_contractor: boolean,
    year_of_production: boolean,
    vehicle_engine_type: boolean,
    model: boolean,
    fuel_consumption: boolean,
    number_of_axles: boolean,
    max_load_capacity: boolean,
    cvs_key: boolean,
};

const DEFAULT_FORM_STATE = {
    consider_all_pages: true,
    type: true,
    mode_of_transport: true,
    truck_plate_number: true,
    trailer_plate_number: true,
    connect_to_cvs: true,
    is_active: true,
    capacity: true,
    euro_norm: true,
    legal_entity: true,
    trailer_capacity: true,
    sub_contractor: true,
    year_of_production: true,
    vehicle_engine_type: true,
    model: true,
    fuel_consumption: true,
    number_of_axles: true,
    max_load_capacity: true,
    vehicle_ext_key: true,
};

// @ts-ignore
function TransportVehicleExcel(props: IFormProps) {
    const { t, visibility, dropdowns } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);

    const onSubmit = async (payloads: TInputForm): Promise<void> => {
        const _params: any = props.moduleState;
        const _payloads: any = payloads;

        if ( payloads.consider_all_pages ) {
            delete _params.page;
            delete _params.per_page;
        }

        try {
            const response = await request({
                url: `/suppliers/transport/vehicles`,
                params: _params,
                paramsSerializer: {
                    serialize: (params: any) => qs.stringify(params)
                },
            });

            delete _payloads.consider_all_pages;
            const columns = pickBy(_payloads, (item: boolean) => ( item ));
            const title: any = getTitles();
            const data: any = [];
            map(response.data.data, (value: any) => {
                const row: any = {
                    type: value.type,
                    mode_of_transport: value.mode_of_transport,
                    truck_plate_number: value.truck_plate_number,
                    trailer_plate_number: value.trailer_plate_number,
                    connect_to_cvs: value.connect_to_cvs ? t('connected') : t('not connected'),
                    is_active: value.is_active ? t('active') : t('inactive'),
                    capacity: value.capacity,
                    euro_norm: value.euro_norm,
                    legal_entity: value.truck_owner?.company_name,
                    trailer_capacity: value.trailer_capacity,
                    sub_contractor: value.sub_contractor?.name,
                    year_of_production: value.year_of_production,
                    vehicle_engine_type: value.vehicle_engine_type,
                    model: value.model,
                    fuel_consumption: value.fuel_consumption,
                    number_of_axles: value.number_of_axles,
                    max_load_capacity: value.max_load_capacity,
                    vehicle_ext_key: value.vehicle_ext_key,
                };

                const _row: any = {};
                map(columns, (_, key: string) => ( _row[key] = row[key] ));
                const arrayOfRow = Object.keys(_row).map((key) => _row[key]);
                data.push(arrayOfRow);
            });

            const _title = map(columns, (_, key: string) => ( title[key] = title[key] ));
            const excel = XLSX.utils.book_new();
            const sheet = XLSX.utils.aoa_to_sheet([_title, ...data]);
            XLSX.utils.book_append_sheet(excel, sheet, t('transport vehicles'));
            XLSX.writeFile(excel, "transport vehicles.xlsx");
        } catch (e: any) {
            props.toastify(t("server error"), "error");
        }
    };

    const onReset = () => {
        const base: any = formRef.current;
        if ( base ) {
            base.resetForm();
            setInitFormState({
                ...DEFAULT_FORM_STATE
            });
        }

        props.setQuery({
            ...props.moduleState,
        });
    };

    const onClose = () => {
        setPanelState(false);

        if ( props.onClose ) {
            props.onClose();
        }
    };

    const getTitles = () => {
        return {
            type: t('type'),
            mode_of_transport: t('mode of transport'),
            truck_plate_number: t('truck plate number'),
            trailer_plate_number: t('trailer plate number'),
            connect_to_cvs: t('cvs status'),
            is_active: t('status'),
            capacity: t('truck capacity') + ' ( ' + t('in kilograms') + ')',
            euro_norm: t('euro norm'),
            legal_entity: t('legal entity'),
            trailer_capacity: t('trailer capacity') + ' ( ' + t('in kilograms') + ')',
            sub_contractor: t('sub contractor'),
            year_of_production: t('year of production'),
            vehicle_engine_type: t('vehicle engine type'),
            model: t('model'),
            fuel_consumption: t('fuel consumption'),
            number_of_axles: t('number of axles'),
            max_load_capacity: t('max load capacity'),
            vehicle_ext_key: t('cvs key'),
        };
    };

    useEffect(() => {
        if ( visibility ) {
            setPanelState(visibility);
            setInitFormState({ ...DEFAULT_FORM_STATE, ...props.moduleState.filters ?? [] });
        }
    }, [visibility]);


    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t("export") } { t("transport vehicles") }</h5>
                            <p>
                                { t("select columns to be exported.") }
                            </p>
                        </div>
                    }
                    visible={ panelState } position="left" onHide={ onClose } blockScroll={ true }
                    style={ { width: '400px' } } closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>
                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4 md:mb-7 sm:mb-7" }>
                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="consider_all_pages"
                                                component={ InputCheckboxField }
                                                label={ t("consider all pages") }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="type"
                                                component={ InputCheckboxField }
                                                label={ t('type') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="mode_of_transport"
                                                component={ InputCheckboxField }
                                                label={ t('mode of transport') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="truck_plate_number"
                                                component={ InputCheckboxField }
                                                label={ t('truck plate number') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="trailer_plate_number"
                                                component={ InputCheckboxField }
                                                label={ t('trailer plate number') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="connect_to_cvs"
                                                component={ InputCheckboxField }
                                                label={ t('cvs status') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="is_active"
                                                component={ InputCheckboxField }
                                                label={ t('status') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="capacity"
                                                component={ InputCheckboxField }
                                                label={ t('capacity') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="euro_norm"
                                                component={ InputCheckboxField }
                                                label={ t('euro norm') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="legal_entity"
                                                component={ InputCheckboxField }
                                                label={ t('legal entity') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="trailer_capacity"
                                                component={ InputCheckboxField }
                                                label={ t('trailer capacity') + ' ( ' + t('in kilograms') + ')' }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="sub_contractor"
                                                component={ InputCheckboxField }
                                                label={ t('sub contractor') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="year_of_production"
                                                component={ InputCheckboxField }
                                                label={ t('year of production') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="vehicle_engine_type"
                                                component={ InputCheckboxField }
                                                label={ t('vehicle engine type') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="model"
                                                component={ InputCheckboxField }
                                                label={ t('model') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="fuel_consumption"
                                                component={ InputCheckboxField }
                                                label={ t('fuel consumption') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="number_of_axles"
                                                component={ InputCheckboxField }
                                                label={ t('number of axles') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="max_load_capacity"
                                                component={ InputCheckboxField }
                                                label={ t('max load capacity') }
                                            />
                                        </Col>

                                        <Col sm={ 12 }>
                                            <Field
                                                inline={ true }
                                                name="vehicle_ext_key"
                                                component={ InputCheckboxField }
                                                label={ t('cvs key') }
                                            />
                                        </Col>
                                    </Row>
                                    <div className="filter filter-card-footer"
                                         style={ { width: '385px' } }>
                                        <Button type={ "submit" } disabled={ props.isSubmitting }
                                                tooltip={ t("apply filter") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-filter me-2" />
                                            { t("filter") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onReset }
                                                tooltip={ t("to make all fields empty") } className={ "ms-2" }
                                                size={ "small" } severity={ "warning" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-refresh me-2" />
                                            { t("reset") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onClose }
                                                tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("close") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

TransportVehicleExcel.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(TransportVehicleExcel);
